import { useEffect, useMemo, useState } from 'react';

import { Box, Button, CircularProgress, InputAdornment, TextField, useTheme } from '@material-ui/core';
import {
  Backdrop,
  CheckboxCheckIconTwoTone,
  CheckInIcon,
  CloseIcon,
  EmptyCameraNotification,
  LoadingSpinnerIconTwoTone,
  StyledDialog,
  useNotificationModal,
} from '@room-match/shared-ui-components';
import QrReader from 'modern-react-qr-reader';
import { useHistory, useParams } from 'react-router-dom';
import { COLORS } from 'shared/constants/COLORS';
import { ROUTES } from 'shared/constants/ROUTES';
import { AuditionPhase } from 'shared/enums/AuditionPhase';
import { AuditionStatus } from 'shared/enums/AuditionStatus';
import { RepresentationType } from 'shared/enums/RepresentationType';
import { SelftapesSteps } from 'shared/enums/SelftapesSteps';
import { useSetHeaderBack, useSetHeaderTitle } from 'shared/hooks/useSetHeader';
import { ISelfTapesScheduleResponse } from 'shared/interfaces/IAuditionSchedule';
import { auditionScheduleService } from 'shared/services/auditionScheduleService';
import { auditionService } from 'shared/services/auditionService';
import { profileService } from 'shared/services/profileService';
import { projectService } from 'shared/services/projectService';
import { ls } from 'shared/utils/ls';
import { useDebounce } from 'use-debounce';
import { Form } from './Form';
import scanIcon from './scan_icon.png';
import { useStyles } from './SelfTapes.styles';
import UpcomingSelftapes from './UpcomingSelftapes';

const { getSingleProject } = projectService();
const { getAuditionSchedules } = auditionScheduleService();
const { getAudition } = auditionService();
const { getProfiles } = profileService();
const { removeLS, getLS } = ls();

const SelfTapes = () => {
  const theme = useTheme();
  const { handleOpen, isOpen, NotificationModal, handleSetMessage, handleChangeModalType } = useNotificationModal({
    type: 'warning',
  });
  const classes = useStyles();
  const history = useHistory();
  const [activeStep, setActiveStep] = useState(0);
  const { projectId, audId, step } = useParams() as { projectId: string; audId: string; step: string; option: string };
  const { data: audData, isLoading: isAudLoading } = getAudition(audId || '');

  const { data: pastSelfTapesData, isLoading: pastSelfTapesLoading } = getAuditionSchedules({
    phase: AuditionPhase.SELF_TAPES,
  });

  const { data: upcomingSelfTapesData, isLoading: upcomingSelfTapesLoading } = getAuditionSchedules({
    phase: AuditionPhase.SELF_TAPES,
    status: AuditionStatus.CONFIRMED,
  });

  const { data: pending, isLoading: pendingLoading } = getAuditionSchedules({
    phase: AuditionPhase.SELF_TAPES_PROCESS,
    status: AuditionStatus.PENDING,
  });

  const { data: dataProfiles } = getProfiles();

  const hasAgency = !!dataProfiles?.data.some(
    (data) => data.attributes.representation_type === RepresentationType.AGENCY_REPRESENTATION,
  );

  const getUpcomingSelfTapesData = useMemo(() => {
    return upcomingSelfTapesData
      ? upcomingSelfTapesData.pages
          .filter((v, index) => index === 0)
          .map((i) => i.data.talent_audition_schedules_connection.nodes)
      : undefined;
  }, [upcomingSelfTapesData]);

  const pendingAudition = useMemo(() => {
    if (pendingLoading) {
      return;
    } else {
      return pending
        ? pending.pages
            .filter((v, index) => index === 0)
            .map((i) => i.data.talent_audition_schedules_connection.nodes)[0]
        : undefined;
    }
  }, [pending, pendingLoading]);

  // const [projectId, setProjectId] = useState('allstate-1775628d16');
  const [projectID, setProjectId] = useState(projectId);
  const [showQR, setshowQR] = useState(false);
  const [auditionData, setauditionData] = useState<ISelfTapesScheduleResponse>();
  const [queryValue] = useDebounce(projectID, 500);
  const { data, isLoading } = getSingleProject(queryValue);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setloading] = useState(true);

  const next = () => {
    if (data && data.data.attributes.self_tapes_roles.data !== null) {
      const roles = data.data.attributes.self_tapes_roles.data;
      let hasRequirements = false;
      roles.map((r) => {
        if (r.attributes.self_tapes_requirements) {
          hasRequirements = true;
        }
      });

      if (hasRequirements && !step) {
        removeLS('prev_url');
        history.push(`${ROUTES.TALENT.SELFTAPES}/${data.data.id}`);
        // captureMessage({
        //   message: 'user self-tapes confirm and next',
        //   data: {
        //     roles,
        //     hasRequirements,
        //   },
        // });
      } else {
        handleChangeModalType('error');
        handleSetMessage(
          'Currently Unavailable',
          `You are unable to submit a self tape at this time as no requirements have been set yet by the casting director, please check with your agency or the casting contact directly.`,
          'OK',
        );
        handleOpen();
        // captureMessage({
        //   message: 'user self-tapes confirm and next',
        //   data: {
        //     roles,
        //     hasRequirements,
        //     status: 'Currently Unavailable',
        //     message:
        //       'You are unable to submit a self tape at this time as no requirements have been set yet by the casting director, please check with your agency or the casting contact directly.',
        //   },
        //   level: 'error',
        // });
      }
    }
  };

  useSetHeaderBack(false);
  useSetHeaderTitle('Self-Tapes');

  const onError = () => {
    setshowQR(false);
  };

  const handleScan = (data: any) => {
    if (data) {
      const id = data.split('/').pop();
      setProjectId(id);
      // captureMessage({
      //   message: 'user self-tapes handle qr scan',
      //   data: { data },
      // });
      setshowQR(false);
      setTimeout(() => {
        setloading(true);
      }, 500);
    }
  };

  useEffect(() => {
    if (data && data.data.attributes.self_tapes_roles.data !== null && projectId) {
      setActiveStep(1);
    } else {
      if (projectId) {
        handleChangeModalType('error');
        handleSetMessage('Invalid Project Code', ``, 'OK');
        handleOpen();
      }
    }
  }, [projectId, data, projectID, isLoading]);

  useEffect(() => {
    if (!data && !isLoading && queryValue) {
      setTimeout(() => {
        setErrorMessage('Project not found');
      }, 100);
    } else {
      setErrorMessage('');
    }
  }, [queryValue, isLoading, data]);

  useEffect(() => {
    switch (step) {
      case SelftapesSteps.SIZE_CARD:
        setActiveStep(1);
        break;
      case SelftapesSteps.QUESTIONS:
        setActiveStep(2);
        break;
      case SelftapesSteps.TERMS:
        setActiveStep(3);
        break;
      case SelftapesSteps.UPLOADS:
        setActiveStep(4);
        break;
      case SelftapesSteps.COMPLETE:
        setActiveStep(5);
        break;
      default:
        break;
    }
  }, [step]);

  const parseLink = (link: string) => {
    const linkArr = link.split('/');
    const pID = linkArr[linkArr.length - 1];
    setProjectId(pID);
  };

  useEffect(() => {
    if (!getLS('prev_url')) removeLS('prev_url');
    return;
  }, []);

  if ((isLoading && projectId) || (audId && isAudLoading)) return <Backdrop isLoading={isLoading} />;

  const handleQRClick = () => {
    navigator.permissions.query({ name: 'camera' as PermissionName }).then((result) => {
      if (navigator.userAgent.includes('Mobile')) {
        return setshowQR(true);
      }
      if (result.state === 'granted') {
        setshowQR(true);
      } else {
        handleChangeModalType('error');
        handleSetMessage('Permission Denied', 'Please allow camera permission in your browser settings', 'OK');
        handleOpen();
      }
    });
  };
  const renderLanding = () => (
    <>
      <div className={classes.mainCont}>
        <div className={classes.root}>
          <Box component="div" className={classes.qrContainer} onClick={handleQRClick}>
            <img
              src={scanIcon}
              style={{
                width: 20,
                height: 20,
                filter: theme.palette.type === 'dark' ? 'invert(1)' : 'invert(0)',
                opacity: 0.8,
              }}
            />
            <p style={{ marginLeft: 10, opacity: 0.7, fontSize: 16 }}>Scan</p>
          </Box>

          <TextField
            value={projectID}
            id="outlined-basic"
            variant="outlined"
            placeholder="Type code…"
            className={classes.input}
            onChange={(e) => {
              if (e.target.value.includes('/')) return parseLink(e.target.value);
              setProjectId(e.target.value);
            }}
            helperText={errorMessage}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CheckInIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {projectID && (
                    <>
                      {isLoading ? (
                        <LoadingSpinnerIconTwoTone style={{ fontSize: 40 }} />
                      ) : data && data.data.attributes.self_tapes_roles.data !== null ? (
                        <CheckboxCheckIconTwoTone style={{ fontSize: 12 }} />
                      ) : (
                        <CloseIcon style={{ fontSize: 12 }} htmlColor={COLORS.ERROR} />
                      )}
                    </>
                  )}
                </>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            fullWidth
            className={classes.buttonCls}
            onClick={next}
            disabled={isLoading || !(data && data.data.attributes.self_tapes_roles.data.length)}
          >
            Confirm & Next
          </Button>
          {/* QR */}
          {showQR && (
            <StyledDialog
              open={showQR}
              onClose={() => {
                setshowQR(false);
                setTimeout(() => {
                  setloading(true);
                }, 500);
              }}
              maxWidth="md"
              aria-labelledby="simple-dialog-title"
            >
              <div style={{ position: 'relative' }}>
                {loading ? (
                  <div
                    style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: 20,
                    }}
                  >
                    <CircularProgress style={{ width: 25, height: 25, marginRight: 10 }} />
                    Initializing...
                  </div>
                ) : (
                  <></>
                )}
                <QrReader
                  facingMode={'environment'}
                  onLoad={() => setloading(false)}
                  delay={500}
                  // style={{ height: 300, width: 30 }}
                  onError={onError}
                  onScan={handleScan}
                  style={{ minWidth: 300, minHeight: 100, width: '100%' }}
                />
              </div>
              {!loading && (
                <span style={{ width: '100%', textAlign: 'center', padding: 10, paddingBottom: 15 }}>
                  Please move the QR Code close the camera
                </span>
              )}
            </StyledDialog>
          )}
          {isOpen && (
            <NotificationModal
              onClose={() => {
                history.push(ROUTES.TALENT.SELFTAPES);
              }}
            />
          )}
        </div>
      </div>

      {getUpcomingSelfTapesData?.length && (
        <UpcomingSelftapes
          isPendingLoading={pendingLoading}
          pending={pendingAudition || []}
          data={(getUpcomingSelfTapesData || [])[0] || []}
          loading={upcomingSelfTapesLoading}
        />
      )}

      {getUpcomingSelfTapesData?.[0].length === 0 ? (
        <EmptyCameraNotification>
          You’ll soon find Self-tape requests here for you.{' '}
          {hasAgency ? 'Ask your agency on how Casting Directors could find you.' : ''}
        </EmptyCameraNotification>
      ) : null}
    </>
  );

  return (
    <>
      {activeStep > 0 && (projectId || projectID) && (data || isLoading) ? (
        <Form
          projectData={data?.data}
          setActiveStep={setActiveStep}
          activeStep={activeStep}
          auditionSchedData={audData?.data}
        />
      ) : (
        renderLanding()
      )}
    </>
  );
};

export default SelfTapes;
